import * as tracking from 'dibs-tracking';
import SV from 'server-vars';
import { LOCALE_US } from 'dibs-intl/exports/locales';

const {
    trackingConstants: { ECOM_PRODUCT_CLICK, ECOM_PRODUCT_IMPRESSION },
    eventNameConstants: { EVENT_SELECT_ITEM, EVENT_VIEW_ITEM_LIST },
} = tracking;

function getProductTrackingData(item, index) {
    const { browseUrl, categoryCode, ecommerceTrackingParams, title } = item?.node || {};
    return {
        position: index + 1,
        category: browseUrl || '',
        categoryCode: categoryCode || '',
        ...(ecommerceTrackingParams || {}),
        name: SV.get('locale') === LOCALE_US ? title : 'n/a',
    };
}

function fireEcommerceImpression(index, itemEdge, list) {
    tracking.trackEcommerce({
        type: ECOM_PRODUCT_IMPRESSION,
        eventName: EVENT_VIEW_ITEM_LIST,
        products: [
            {
                list,
                ...getProductTrackingData(itemEdge, index),
            },
        ],
    });
}

function fireEcommerceImpressions(itemEdges, list, startingIndex = 0) {
    tracking.trackEcommerce({
        type: ECOM_PRODUCT_IMPRESSION,
        eventName: EVENT_VIEW_ITEM_LIST,
        products: itemEdges.map((itemEdge, index) => ({
            list,
            ...getProductTrackingData(itemEdge, startingIndex + index),
        })),
    });
}

function fireEcommerceClick(index, itemEdge, list) {
    tracking.trackEcommerce({
        type: ECOM_PRODUCT_CLICK,
        eventName: EVENT_SELECT_ITEM,
        products: [
            {
                ...getProductTrackingData(itemEdge, index),
            },
        ],
        actionField: { list, itemId: itemEdge?.node?.serviceId },
    });
}

/**
 * @see PdpRespSimilarItemsLanding.jsx
 */
const LANDING_ACTION = 'landing module clicked';
const LANDING_CATEGORY = 'landing module interaction';
const LANDING_LIST = 'pdp browse similar items';
const LANDING_LIST_SOLD = 'sold pdp browse similar items';

export function trackLandingItemImpressions(landingItems, isSoldPdp) {
    fireEcommerceImpressions(landingItems, isSoldPdp ? LANDING_LIST_SOLD : LANDING_LIST);
}

export function trackItemClick({ moduleType = '', actionValue = '' } = {}, event) {
    const label = `related items${moduleType && ' - ' + moduleType}`;
    const action = `${LANDING_ACTION}${actionValue && ' - ' + actionValue}`;
    tracking.trackEvent(
        {
            label,
            action,
            category: LANDING_CATEGORY,
        },
        event || null
    );
}

export function trackEcommerceLandingItemClick({ index, landingItem, isSoldPdp }) {
    fireEcommerceClick(index, landingItem, isSoldPdp ? LANDING_LIST_SOLD : LANDING_LIST);
}

export function trackLandingViewAllClick(event) {
    tracking.trackEvent(
        {
            label: 'related items view all',
            action: LANDING_ACTION,
            category: LANDING_CATEGORY,
        },
        event || null
    );
}

/**
 * @see PdpRespSimilarItemsGrid.jsx
 */
export const SIMILAR_ITEMS_LIST = 'pdp you may also like';
export const VISUALLY_SIMILAR_ITEMS_LIST = 'pdp visually similar';
export const COLLECTION_MODULE_ITEMS_LIST = 'pdp collection module';
export const PERSONALIZATION_MODULE_ITEMS_LIST = 'pdp personalization module';
export const PDP_MORE_FROM_SELLER_LIST = 'pdp more from seller';
export const PDP_SOLD_MORE_FROM_SELLER_LIST = 'sold pdp more from seller';

export function trackSimilarItemImpressions(
    gridItems,
    location = SIMILAR_ITEMS_LIST,
    startingIndex = 0
) {
    const items = gridItems.map(item => ({ node: item }));
    fireEcommerceImpressions(items, location, startingIndex);
}

export function trackSimilarItemImpression(index, gridItem, location = SIMILAR_ITEMS_LIST) {
    const item = { node: gridItem };
    fireEcommerceImpression(index, item, location);
}

export function trackSimilarItemClick(
    index,
    gridItem,
    location = SIMILAR_ITEMS_LIST,
    trackingVariant
) {
    const item = { node: gridItem };
    fireEcommerceClick(index, item, location, trackingVariant);
}

const COMPLETE_THE_LOOK_LIST = 'pdp complete the look';
export function trackComplementaryItemClick(index, gridItem) {
    fireEcommerceClick(index, gridItem, COMPLETE_THE_LOOK_LIST);
}

export function trackComplementaryItemImpressions(gridItems) {
    fireEcommerceImpressions(gridItems, COMPLETE_THE_LOOK_LIST);
}

export function trackComplementaryGridViewEvent() {
    tracking.trackEvent({
        category: 'promo interaction',
        action: 'complete the look list displayed',
        label: 'complete the look',
    });
}

export function trackComplementaryGridToggleExpansionEvent(expanding = true, gridItems = null) {
    tracking.trackEvent({
        category: 'promo interaction',
        action: 'complete the look view button clicked',
        label: expanding ? 'view more' : 'view less',
    });

    if (expanding && gridItems) {
        trackComplementaryItemImpressions(gridItems);
    }
}

export function trackComplementaryItemClickEvent() {
    tracking.trackEvent({
        category: 'promo interaction',
        action: 'complete the look list clicked',
        label: 'complete the look',
    });
}

/**
 * @see PdpRespSellerItemsGrid.jsx
 */
export const SELLER_ITEMS_LIST = 'pdp more from dealer';
export function trackSellerItemImpressions(gridSellerItems) {
    fireEcommerceImpressions(gridSellerItems, SELLER_ITEMS_LIST);
}

export function trackSellerItemClick(index, gridSellerItem, location = SELLER_ITEMS_LIST) {
    fireEcommerceClick(index, gridSellerItem, location);
}

export function trackSellerPageClick(event) {
    tracking.trackEvent(
        {
            action: 'click to seller page',
            category: 'dealer click',
            label: 'PDP',
        },
        event || null
    );
}

export function trackItemsDisplayed({ moduleType = '', actionValue = '' } = {}) {
    const label = `related items${moduleType && ' - ' + moduleType}`;
    const action = `landing module displayed${actionValue && ' - ' + actionValue}`;
    tracking.trackEvent({
        action,
        label,
        category: LANDING_CATEGORY,
    });
}

export function trackViewMoreRelatedItemsClick(event) {
    tracking.trackEvent(
        {
            action: 'YMAL view button clicked',
            category: 'promo interaction',
            label: 'view more',
        },
        event || null
    );
}

export function trackViewLessRelatedItemsClick(event) {
    tracking.trackEvent(
        {
            action: 'YMAL view button clicked',
            category: 'promo interaction',
            label: 'view less',
        },
        event || null
    );
}

export function trackViewMoreVisuallySimilarClick(event) {
    tracking.trackEvent(
        {
            action: 'view more clicked',
            category: 'product',
            label: VISUALLY_SIMILAR_ITEMS_LIST,
        },
        event || null
    );
}
export function trackCarouselEvent(direction) {
    tracking.trackEvent(
        {
            label: direction,
            isInteractiveEvent: true,
            category: 'promo interaction',
            action: 'more from seller arrow clicked',
        },
        null
    );
}

export function trackCarouselItemImpression(trackingObj, list = 'pdp more from dealer') {
    tracking.trackEcommerce({
        type: ECOM_PRODUCT_IMPRESSION,
        eventName: EVENT_VIEW_ITEM_LIST,
        actionField: {
            list,
        },
        products: [
            {
                ...trackingObj,
                list,
            },
        ],
    });
}

export function trackCarouselItemImpressions({
    visibleItems,
    startIndex,
    impressionTrackingRef,
    list,
}) {
    (visibleItems || []).forEach((visibleItem, index) => {
        const { ecommerceTrackingParams, categoryCode, categoryPath } = visibleItem || {};
        const { id } = ecommerceTrackingParams || {};
        if (!impressionTrackingRef.current[id]) {
            trackCarouselItemImpression(
                {
                    ...ecommerceTrackingParams,
                    category: categoryPath,
                    categoryCode,
                    position: Math.floor(startIndex + index),
                },
                list
            );
            impressionTrackingRef.current[id] = true;
        }
    });
}

export function trackViewAllCollectionItemsClick(event) {
    tracking.trackEvent(
        {
            action: 'collections view all button click',
            category: 'promo interaction',
            label: 'pdp',
        },
        event || null
    );
}

export const trackCollectionItemsVisible = () => {
    tracking.trackEvent({
        action: 'collections module reached',
        category: 'product',
        label: 'pdp',
    });
};

export const trackCollectionItemsRendered = () => {
    tracking.trackEvent({
        action: 'collections module present',
        category: 'product',
        label: 'pdp',
        isInteractiveEvent: false,
    });
};

export const trackPersonalizedItemsVisible = () => {
    tracking.trackEvent({
        action: 'personalization module reached',
        category: 'product',
        label: 'pdp',
    });
};

export const trackPersonalizedItemsRendered = () => {
    tracking.trackEvent({
        action: 'personalization module present',
        category: 'product',
        label: 'pdp',
        isInteractiveEvent: false,
    });
};

export const trackBottomYmalModuleReached = () => {
    tracking.trackEvent({
        action: 'bottom YMAL module reached',
        category: 'product',
        label: 'pdp',
        isInteractiveEvent: false,
    });
};
